import { isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { Badge } from 'components/Blotters/Filter/Badges/Badge';
import { FilterBadge } from 'components/Blotters/Filter/Badges/FilterBadge';
import { Filter } from 'components/Blotters/Filter/Badges/hooks';
import type { ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import { useIntl } from 'react-intl';
import { ObjectWithDefinedValue } from 'types/object';

type GridSummaryProps = {
  filters: Filter[];
  sortCols: ObjectWithDefinedValue<ColumnPreferenceState, 'sort'>[];
  translationFunc: (colId: string) => IntlKey;
  onDismiss: (colId: string) => void;
  onReset: () => void;
  className?: string;
};

export function GridSummary({ filters, sortCols, className, translationFunc, onDismiss, onReset }: GridSummaryProps) {
  const hasSort = isNonEmpty(sortCols);
  const hasFilters = isNonEmpty(filters);
  const { formatMessage } = useIntl();

  return !hasFilters && !hasSort ? null : (
    <div
      className={classNames('d-flex align-items-center py-1 px-3 border-top gap-2', className)}
      data-e2e="filters-badges"
    >
      {hasFilters ? <span>{formatMessage({ id: 'blotter.badges.filters.appliedLabel' })}</span> : undefined}
      {filters.map(f => (
        <FilterBadge column={f} key={f.colId} translationFunc={translationFunc} onDismiss={onDismiss} />
      ))}
      {hasSort ? <span>{formatMessage({ id: 'blotter.badges.sort.appliedLabel' })}</span> : undefined}
      {sortCols.map(col => (
        <Badge id={col.colId} translationFunc={translationFunc} key={col.colId} className="m-1">
          <FormattedMessage id={`blotter.badges.sort.${col.sort}`} />
        </Badge>
      ))}
      {filters.length > 1 || hasSort ? (
        <button className="btn btn-flat btn-sm" onClick={onReset} data-e2e="clear-filters-btn">
          {formatMessage({ id: 'clearAll' })}
        </button>
      ) : undefined}
    </div>
  );
}

GridSummary.displayName = 'GridSummary';
